import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Users, ShoppingBag, ShieldCheck, Wallet, TrendingUp, Loader2, FileText,
  CheckCircle2, XCircle, Trash2, Search, ExternalLink, AlertTriangle, BadgeCheck, LifeBuoy, Mail, Eye, Play, Send, Download, Hammer, Star,
} from "lucide-react";
import { toast } from "sonner";
import FilmAnalyticsWidget from "@/components/admin/FilmAnalyticsWidget";
import CuratedListings from "@/components/admin/CuratedListings";
import SupplierOrders from "@/components/admin/SupplierOrders";
import PromoCodes from "@/components/admin/PromoCodes";
import SignupAnalytics from "@/components/admin/SignupAnalytics";
import Broadcast from "@/components/admin/Broadcast";
import CJDropshipping from "@/components/admin/CJDropshipping";

const StatCard = ({ icon: Icon, label, value, hint, accent }) => (
  <Card className="p-5 bg-white border-black/10">
    <div className="flex items-start justify-between">
      <div>
        <div className="label-overline text-muted-foreground">{label}</div>
        <div className="font-display text-3xl mt-2">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
      <Icon className={`w-5 h-5 ${accent || "text-[#C05A3C]"}`}/>
    </div>
  </Card>
);

const Admin = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [makerShowcases, setMakerShowcases] = useState([]);
  const [uQuery, setUQuery] = useState("");
  const [lQuery, setLQuery] = useState("");
  const [reviewing, setReviewing] = useState(null); // {kind:'user'|'listing', target, action}
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [docModal, setDocModal] = useState(null); // user being viewed
  const [exporting, setExporting] = useState(null);
  const [exportSince, setExportSince] = useState("");
  const [exportUntil, setExportUntil] = useState("");

  const loadAll = async () => {
    try {
      const [s, pu, au, pl, al, tx] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users?status=pending"),
        api.get("/admin/users", { params: { q: uQuery || undefined } }),
        api.get("/admin/listings?status=pending"),
        api.get("/admin/listings", { params: { q: lQuery || undefined } }),
        api.get("/admin/transactions"),
      ]);
      setStats(s.data);
      setPendingUsers(pu.data);
      setAllUsers(au.data);
      setPendingListings(pl.data);
      setAllListings(al.data);
      setTransactions(tx.data);
      api.get("/admin/makers/showcases")
        .then((r) => setMakerShowcases(r.data?.items || []))
        .catch(() => {});
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };

  useEffect(() => { if (user?.role === "admin") loadAll(); /* eslint-disable-line */ }, [user]);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const openReview = (kind, target, action) => {
    setReviewing({ kind, target, action });
    setNotes("");
  };

  const submitReview = async () => {
    if (!reviewing) return;
    setBusy(true);
    try {
      const { kind, target, action } = reviewing;
      if (kind === "user-verify") {
        await api.post(`/admin/users/${target.user_id}/verify`, { approve: action === "approve", notes });
        toast.success(action === "approve" ? "User verified" : "User rejected");
      } else if (kind === "user-remove") {
        await api.post(`/admin/users/${target.user_id}/remove`, { approve: action === "remove", notes });
        toast.success(action === "remove" ? "User removed" : "User restored");
      } else if (kind === "listing-approve") {
        await api.post(`/admin/listings/${target.listing_id}/approve`, { approve: action === "approve", notes });
        toast.success(action === "approve" ? "Listing approved" : "Listing rejected");
      } else if (kind === "listing-delete") {
        await api.delete(`/admin/listings/${target.listing_id}`);
        toast.success("Listing removed");
      } else if (kind === "listing-restore") {
        await api.post(`/admin/listings/${target.listing_id}/restore`);
        toast.success("Listing restored");
      }
      setReviewing(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const openDocs = async (u) => {
    try {
      const { data } = await api.get(`/admin/user/${u.user_id}/docs`);
      setDocModal({ user: u, ...data });
    } catch { toast.error("Could not load documents"); }
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(`Digest sent to ${data.sent_to.join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send digest");
    }
  };

  const previewDigest = () => {
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/digest/preview`;
    // Open in new tab with auth header via fetch + blob (since <a> can't send headers)
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const win = window.open("", "_blank");
        win.document.write(html);
        win.document.close();
      })
      .catch(() => toast.error("Could not load digest preview"));
  };

  const pingSitemap = async () => {
    try {
      const { data } = await api.post("/admin/seo/ping-sitemap");
      const indexnow = data?.engines?.indexnow;
      if (indexnow?.ok) {
        toast.success(`Sitemap submitted: ${indexnow.submitted} URLs accepted by IndexNow (Bing/Yandex)`);
      } else {
        toast.error(indexnow?.note || indexnow?.reason || "IndexNow submission failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to ping search engines");
    }
  };

  const exportCsv = async (kind) => {
    setExporting(kind);
    try {
      const params = {};
      if (exportSince) params.since = exportSince;
      if (exportUntil) params.until = exportUntil;
      const res = await api.get(`/admin/export/${kind}.csv`, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const rangeBit = exportSince && exportUntil
        ? `-${exportSince}_to_${exportUntil}`
        : exportSince ? `-from-${exportSince}`
        : exportUntil ? `-until-${exportUntil}` : "";
      a.download = `afrimarket-${kind}${rangeBit}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} CSV downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to export ${kind}`);
    } finally {
      setExporting(null);
    }
  };

  const flagShowcase = async (showcaseId, changes) => {
    try {
      await api.post(`/admin/makers/showcases/${showcaseId}/flag`, null, { params: changes });
      setMakerShowcases((rows) =>
        rows.map((r) => (r.showcase_id === showcaseId ? { ...r, ...changes } : r)),
      );
      toast.success("Showcase updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update showcase");
    }
  };

  return (
    <div className="container-narrow py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-overline text-[#C05A3C] mb-1">Admin</div>
          <h1 className="font-display text-4xl">Control room</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={previewDigest} data-testid="digest-preview-btn" className="border-[#1A2024] text-[#1A2024]"><Eye className="w-4 h-4 mr-2"/> Preview digest</Button>
          <Button variant="outline" size="sm" onClick={sendDigest} data-testid="digest-send-btn" className="border-[#4A5D23] text-[#4A5D23]"><Mail className="w-4 h-4 mr-2"/> Send digest now</Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="plyx-digest-send-btn"
            onClick={async () => {
              try {
                await api.post("/admin/plyx/send-creator-digest-now");
                toast.success("Plyx creator digest queued");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to queue Plyx digest");
              }
            }}
            className="border-[#FF2D7A] text-[#FF2D7A]"
          >
            <Mail className="w-4 h-4 mr-2"/> Send Plyx digest now
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="plyx-top-gifter-send-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/plyx/send-top-gifter-digest-now");
                toast.success(`Top-gifter digest: ${data.sent} sent · ${data.total_creators} creators · Slack ${data.slack_posted ? "posted ✓" : "skipped"}`);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to send top-gifter digest");
              }
            }}
            className="border-[#FFB42D] text-[#FFB42D]"
          >
            <Mail className="w-4 h-4 mr-2"/> Send Top-Gifter digest
          </Button>
          <Button variant="outline" size="sm" onClick={pingSitemap} data-testid="seo-ping-btn" className="border-[#D9923B] text-[#D9923B]"><Send className="w-4 h-4 mr-2"/> Submit sitemap to search engines</Button>
          <Link to="/share-ad" data-testid="share-ad-link">
            <Button variant="outline" size="sm" className="border-[#C05A3C] text-[#C05A3C]"><Play className="w-4 h-4 mr-2"/> Video ad</Button>
          </Link>
          <Link to="/admin/revenue" data-testid="admin-revenue-link">
            <Button variant="outline" className="border-[#1A2024] text-[#1A2024]"><TrendingUp className="w-4 h-4 mr-2"/> Revenue dashboard</Button>
          </Link>
          <Link to="/admin/complaints" data-testid="admin-complaints-link">
            <Button variant="outline" className="border-[#C05A3C] text-[#C05A3C]"><LifeBuoy className="w-4 h-4 mr-2"/> Cases queue</Button>
          </Link>
          <Badge className="bg-[#1A2024] text-white">Logged in as {user.email}</Badge>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <>
          {/* Hero — Registered users (most prominent) */}
          <Card className="p-6 bg-gradient-to-br from-[#1A2024] via-[#233348] to-[#2A3D36] text-white border-0 mb-4" data-testid="hero-registered-users">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <div className="label-overline text-white/70">Registered users on AfriMarket</div>
                <div className="font-display text-6xl mt-2 leading-none" data-testid="total-users-value">{stats.total_users.toLocaleString()}</div>
                <div className="text-sm text-white/70 mt-2">
                  <span className="text-[#F2C94C] font-semibold">+{stats.new_users_today}</span> today &nbsp;·&nbsp;
                  <span className="text-[#F2C94C] font-semibold">+{stats.new_users_week}</span> this week
                </div>
              </div>
              <div className="flex gap-6 text-sm">
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">Verified sellers</div>
                  <div className="font-display text-2xl" data-testid="verified-users-value">{stats.verified_users}</div>
                </div>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">Pro members</div>
                  <div className="font-display text-2xl" data-testid="pro-users-value">{stats.pro_users}</div>
                </div>
              </div>
            </div>
          </Card>

          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
            <StatCard icon={ShieldCheck} label="Pending KYC" value={stats.pending_verifications} hint="awaiting your review"/>
            <StatCard icon={FileText} label="Pending listings" value={stats.pending_listings}/>
            <StatCard icon={ShoppingBag} label="Live listings" value={stats.total_listings} hint={`+${stats.new_listings_week} this week`}/>
            <StatCard icon={LifeBuoy} label="Open cases" value={stats.open_complaints} accent="text-[#B84A28]"/>
            <StatCard icon={TrendingUp} label="GMV" value={`$${stats.gmv.toFixed(0)}`} hint="gross merchandise value"/>
            <StatCard icon={Wallet} label="Platform fees" value={`$${stats.platform_revenue.toFixed(2)}`} accent="text-[#4A5D23]" hint={`${stats.paid_transactions} txns`}/>
          </div>

          {/* CSV export date range — applies to all Export CSV buttons below */}
          <Card className="p-3 mb-4 bg-[#F3EFE9]/60 border-black/10 flex flex-wrap items-center gap-3" data-testid="export-date-range">
            <span className="label-overline text-muted-foreground">CSV export range</span>
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">From</span>
              <Input type="date" value={exportSince} onChange={(e) => setExportSince(e.target.value)} className="h-9 w-44" data-testid="export-since"/>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">To</span>
              <Input type="date" value={exportUntil} onChange={(e) => setExportUntil(e.target.value)} className="h-9 w-44" data-testid="export-until"/>
            </label>
            {(exportSince || exportUntil) && (
              <button type="button" onClick={() => { setExportSince(""); setExportUntil(""); }} className="text-xs text-[#C05A3C] hover:underline" data-testid="export-range-clear">Clear</button>
            )}
            <span className="text-xs text-muted-foreground ml-auto">Leave blank to export everything · applied to the tabs below</span>
          </Card>
        </>
      )}

      {/* Brand film funnel — Impressions → Expansions → Full-film clicks */}
      <FilmAnalyticsWidget />

      {/* CJ Dropshipping — search + import auto-fulfilled products */}
      <div className="mb-6">
        <CJDropshipping />
      </div>

      {/* Curated dropshipping listings */}
      <div className="mb-6">
        <CuratedListings />
      </div>

      {/* Autopilot supplier orders — paid curated orders awaiting forward/ship */}
      <div className="mb-6">
        <SupplierOrders />
      </div>

      {/* Promo codes */}
      <div className="mb-6">
        <PromoCodes />
      </div>

      <Tabs defaultValue="kyc" className="w-full">
        <TabsList className="bg-white border border-black/10 p-1">
          <TabsTrigger value="kyc" data-testid="admin-tab-kyc">KYC Queue <Badge className="ml-2 bg-[#C05A3C] text-white">{pendingUsers.length}</Badge></TabsTrigger>
          <TabsTrigger value="listings" data-testid="admin-tab-listings">Listings <Badge className="ml-2 bg-[#C05A3C] text-white">{pendingListings.length}</Badge></TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">All users</TabsTrigger>
          <TabsTrigger value="all-listings" data-testid="admin-tab-all-listings">All listings</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="admin-tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="makers" data-testid="admin-tab-makers">Makers <Badge className="ml-2 bg-[#C05A3C] text-white">{makerShowcases.length}</Badge></TabsTrigger>
          <TabsTrigger value="signups" data-testid="admin-tab-signups">Signups</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="admin-tab-broadcast">Broadcast</TabsTrigger>
        </TabsList>

        {/* KYC tab */}
        <TabsContent value="kyc" className="mt-6">
          {pendingUsers.length === 0 ? (
            <Card className="p-10 text-center bg-white border-black/10">
              <CheckCircle2 className="w-10 h-10 mx-auto text-[#4A5D23] mb-2"/>
              <h3 className="font-display text-2xl">All caught up</h3>
              <p className="text-muted-foreground">No sellers waiting for verification.</p>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="kyc-queue">
              {pendingUsers.map((u) => (
                <Card key={u.user_id} className="p-5 bg-white border-black/10 flex flex-col md:flex-row md:items-center gap-4">
                  <Avatar className="w-12 h-12"><AvatarImage src={u.picture}/><AvatarFallback className="bg-[#233348] text-white">{u.name?.slice(0,1)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{u.email} · {u.country || "—"}</div>
                    {u.full_legal_name && <div className="text-xs mt-1">Legal: {u.full_legal_name}</div>}
                    {u.address_text && <div className="text-xs text-muted-foreground">{u.address_text}</div>}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openDocs(u)} data-testid={`view-docs-${u.user_id}`}><ExternalLink className="w-3 h-3 mr-1"/> View docs</Button>
                    <Button size="sm" onClick={() => openReview("user-verify", u, "approve")} className="bg-[#4A5D23] hover:bg-[#4A5D23]/90 text-white" data-testid={`approve-user-${u.user_id}`}><CheckCircle2 className="w-3 h-3 mr-1"/> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => openReview("user-verify", u, "reject")} className="border-[#B84A28] text-[#B84A28]" data-testid={`reject-user-${u.user_id}`}><XCircle className="w-3 h-3 mr-1"/> Reject</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Listings approval */}
        <TabsContent value="listings" className="mt-6">
          {pendingListings.length === 0 ? (
            <Card className="p-10 text-center bg-white border-black/10">
              <CheckCircle2 className="w-10 h-10 mx-auto text-[#4A5D23] mb-2"/>
              <h3 className="font-display text-2xl">No listings to approve</h3>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="listing-queue">
              {pendingListings.map((l) => (
                <Card key={l.listing_id} className="p-4 bg-white border-black/10 flex gap-4 items-center">
                  <Link to={`/listing/${l.listing_id}`} className="shrink-0 w-20 h-20 bg-[#F3EFE9] rounded-md overflow-hidden">
                    {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover"/>}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-[#F3EFE9] text-[#2A3D36] capitalize mb-1">{l.type}</Badge>
                    <Link to={`/listing/${l.listing_id}`} className="font-display text-lg hover:text-[#C05A3C] block truncate">{l.title}</Link>
                    <div className="text-xs text-muted-foreground">{l.owner_name} · {l.country} · {l.currency} {l.price}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1 mt-1">{l.description}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => openReview("listing-approve", l, "approve")} className="bg-[#4A5D23] hover:bg-[#4A5D23]/90 text-white" data-testid={`approve-listing-${l.listing_id}`}><CheckCircle2 className="w-3 h-3 mr-1"/> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => openReview("listing-approve", l, "reject")} className="border-[#B84A28] text-[#B84A28]"><XCircle className="w-3 h-3 mr-1"/> Reject</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* All users */}
        <TabsContent value="users" className="mt-6 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/>
              <Input value={uQuery} onChange={(e) => setUQuery(e.target.value)} placeholder="Search users by email or name" className="pl-9" data-testid="admin-user-search"/>
            </div>
            <Button onClick={loadAll}>Search</Button>
            <Button variant="outline" onClick={() => exportCsv("users")} disabled={exporting === "users"} data-testid="export-users-csv" className="border-[#4A5D23] text-[#4A5D23]">
              {exporting === "users" ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Download className="w-4 h-4 mr-2"/>}
              Export CSV
            </Button>
          </div>
          <div className="text-sm font-medium text-foreground px-1" data-testid="admin-users-total">
            {allUsers.length} {allUsers.length === 1 ? "user" : "users"} total
          </div>
          <Card className="bg-white border-black/10 overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-[#F3EFE9] text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="text-left p-3">User</th><th className="text-left p-3">Status</th><th className="text-left p-3">Role</th><th className="text-left p-3">Registered from</th><th className="text-left p-3">Joined</th><th className="text-right p-3">Action</th></tr>
              </thead>
              <tbody>
                {allUsers.map((u) => (
                  <tr key={u.user_id} className="border-t border-black/5">
                    <td className="p-3">
                      <div className="font-medium">{u.name} {u.verified_badge && <BadgeCheck title="badge"/>}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                      {(u.whatsapp || u.phone) && <div className="text-xs text-muted-foreground">{u.whatsapp || u.phone}</div>}
                    </td>
                    <td className="p-3">
                      {u.is_removed ? <Badge className="bg-[#B84A28]/15 text-[#B84A28]">Removed</Badge>
                        : <Badge className="bg-[#F3EFE9] text-foreground capitalize">{u.verification_status}</Badge>}
                    </td>
                    <td className="p-3 capitalize">{u.role}</td>
                    <td className="p-3 text-xs" data-testid={`user-country-${u.user_id}`}>
                      {u.signup_country_geo ? (
                        <>
                          <div className="font-medium">{u.signup_country_geo}</div>
                          {(u.signup_city || u.signup_region) && (
                            <div className="text-muted-foreground">{[u.signup_city, u.signup_region].filter(Boolean).join(", ")}</div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="font-medium text-muted-foreground">— not detected</div>
                          {u.country && u.country.trim() && (
                            <div className="text-muted-foreground italic">declared: {u.country.trim()}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{u.created_at?.slice(0,10)}</td>
                    <td className="p-3 text-right">
                      {u.role !== "admin" && (
                        u.is_removed ? (
                          <Button variant="outline" size="sm" onClick={() => openReview("user-remove", u, "restore")} data-testid={`restore-${u.user_id}`}>Restore</Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => openReview("user-remove", u, "remove")} className="border-[#B84A28] text-[#B84A28]" data-testid={`remove-${u.user_id}`}><Trash2 className="w-3 h-3 mr-1"/> Remove</Button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* All listings */}
        <TabsContent value="all-listings" className="mt-6 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/>
              <Input value={lQuery} onChange={(e) => setLQuery(e.target.value)} placeholder="Search listings" className="pl-9" data-testid="admin-listing-search"/>
            </div>
            <Button onClick={loadAll}>Search</Button>
            <Button variant="outline" onClick={() => exportCsv("listings")} disabled={exporting === "listings"} data-testid="export-listings-csv" className="border-[#4A5D23] text-[#4A5D23]">
              {exporting === "listings" ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Download className="w-4 h-4 mr-2"/>}
              Export CSV
            </Button>
          </div>
          <div className="space-y-2">
            {allListings.map((l) => (
              <Card key={l.listing_id} className="p-3 bg-white border-black/10 flex gap-3 items-center">
                <div className="w-14 h-14 bg-[#F3EFE9] rounded overflow-hidden shrink-0">{l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover"/>}</div>
                <div className="flex-1 min-w-0">
                  <Link to={`/listing/${l.listing_id}`} className="font-medium hover:text-[#C05A3C] truncate block">{l.title}</Link>
                  <div className="text-xs text-muted-foreground">{l.owner_name} · {l.country} · {l.currency} {l.price}</div>
                </div>
                <Badge className="bg-[#F3EFE9] text-foreground capitalize">{l.approval_status || "approved"}</Badge>
                {l.status === "removed" ? (
                  <>
                    <Badge className="bg-[#B84A28]/15 text-[#B84A28]">Removed</Badge>
                    <Button size="sm" variant="outline" onClick={() => openReview("listing-restore", l, "restore")} className="border-[#4A5D23] text-[#4A5D23]" data-testid={`restore-listing-${l.listing_id}`}>Restore</Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => openReview("listing-delete", l, "delete")} className="border-[#B84A28] text-[#B84A28]" data-testid={`del-listing-${l.listing_id}`}><Trash2 className="w-3 h-3"/></Button>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Transactions */}
        <TabsContent value="transactions" className="mt-6 space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => exportCsv("transactions")} disabled={exporting === "transactions"} data-testid="export-transactions-csv" className="border-[#4A5D23] text-[#4A5D23]">
              {exporting === "transactions" ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Download className="w-4 h-4 mr-2"/>}
              Export CSV
            </Button>
          </div>
          <Card className="bg-white border-black/10 overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-tx-table">
              <thead className="bg-[#F3EFE9] text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="text-left p-3">When</th><th className="text-left p-3">Type</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Fee</th><th className="text-left p-3">Status</th></tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.transaction_id} className="border-t border-black/5">
                    <td className="p-3 text-xs">{t.created_at?.slice(0,16).replace("T"," ")}</td>
                    <td className="p-3 capitalize">{t.payment_type || "listing"}</td>
                    <td className="p-3">{t.currency?.toUpperCase()} {t.amount}</td>
                    <td className="p-3">{t.platform_fee ? `${t.currency?.toUpperCase()} ${t.platform_fee}` : "—"}</td>
                    <td className="p-3"><Badge className={t.payment_status === "paid" ? "bg-[#4A5D23]/15 text-[#4A5D23]" : "bg-[#F3EFE9]"}>{t.payment_status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {transactions.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No transactions yet.</div>}
          </Card>
        </TabsContent>

        {/* Makers moderation */}
        <TabsContent value="makers" className="mt-6">
          {makerShowcases.length === 0 ? (
            <Card className="p-10 text-center bg-white border-black/10">
              <Hammer className="w-10 h-10 mx-auto text-[#C05A3C] mb-2"/>
              <h3 className="font-display text-2xl">No maker showcases yet</h3>
              <p className="text-muted-foreground">Submissions to the African Makers Exhibition will appear here.</p>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="admin-makers-list">
              {makerShowcases.map((m) => (
                <Card key={m.showcase_id} className="p-3 bg-white border-black/10 flex gap-3 items-center" data-testid={`admin-maker-${m.showcase_id}`}>
                  <Link to={`/makers/${m.showcase_id}`} className="w-16 h-16 bg-[#F3EFE9] rounded overflow-hidden shrink-0">
                    {m.image_urls?.[0] && <img src={m.image_urls[0]} alt="" className="w-full h-full object-cover"/>}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/makers/${m.showcase_id}`} className="font-medium hover:text-[#C05A3C] truncate block">{m.title}</Link>
                    <div className="text-xs text-muted-foreground capitalize">{m.creator_name} · {m.category} · {m.country}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{m.sponsor_count} interested · {m.view_count} views</div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    {m.is_removed && <Badge className="bg-[#B84A28]/15 text-[#B84A28]">Removed</Badge>}
                    {!m.is_published && !m.is_removed && <Badge className="bg-[#F3EFE9] text-foreground">Hidden</Badge>}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => flagShowcase(m.showcase_id, { is_featured: !m.is_featured })}
                      data-testid={`maker-feature-${m.showcase_id}`}
                      className={m.is_featured ? "border-[#D9923B] text-[#D9923B] bg-[#D9923B]/10" : "border-black/15"}
                    >
                      <Star className={`w-3 h-3 mr-1 ${m.is_featured ? "fill-[#D9923B]" : ""}`}/> {m.is_featured ? "Featured" : "Feature"}
                    </Button>
                    {m.is_removed ? (
                      <Button size="sm" variant="outline" onClick={() => flagShowcase(m.showcase_id, { is_removed: false, is_published: true })} data-testid={`maker-restore-${m.showcase_id}`}>Restore</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => flagShowcase(m.showcase_id, { is_removed: true, is_published: false })} className="border-[#B84A28] text-[#B84A28]" data-testid={`maker-remove-${m.showcase_id}`}><Trash2 className="w-3 h-3"/></Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Signups analytics */}
        <TabsContent value="signups" className="mt-6">
          <SignupAnalytics />
        </TabsContent>

        {/* Marketing broadcast */}
        <TabsContent value="broadcast" className="mt-6">
          <Broadcast />
        </TabsContent>
      </Tabs>

      {/* Review dialog */}
      <Dialog open={!!reviewing} onOpenChange={(o) => { if (!o && !busy) setReviewing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {reviewing?.kind === "user-verify" && (reviewing.action === "approve" ? "Approve seller?" : "Reject verification?")}
              {reviewing?.kind === "user-remove" && (reviewing.action === "remove" ? "Remove this user?" : "Restore this user?")}
              {reviewing?.kind === "listing-approve" && (reviewing.action === "approve" ? "Approve listing?" : "Reject listing?")}
              {reviewing?.kind === "listing-delete" && "Remove this listing?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {reviewing?.target && (
              <div className="p-3 bg-[#F3EFE9] rounded-md text-sm">
                {reviewing.target.name || reviewing.target.title}
              </div>
            )}
            {reviewing?.action !== "delete" && (
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (sent to user)" rows={3} data-testid="review-notes"/>
            )}
            {(reviewing?.action === "remove" || reviewing?.action === "reject" || reviewing?.kind === "listing-delete") && (
              <div className="flex items-center gap-2 text-xs text-[#B84A28]"><AlertTriangle className="w-3 h-3"/> This action notifies the user.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitReview} disabled={busy} className="bg-[#C05A3C] hover:bg-[#D46B4E]" data-testid="review-confirm">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> Submitting…</> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Docs modal */}
      <Dialog open={!!docModal} onOpenChange={(o) => !o && setDocModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Verification documents</DialogTitle></DialogHeader>
          {docModal && (
            <div className="space-y-4 text-sm">
              <div><span className="font-semibold">Legal name:</span> {docModal.full_legal_name || "—"}</div>
              <div><span className="font-semibold">Address:</span> {docModal.address_text || "—"}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <DocPreview label="Government ID" path={docModal.id_doc_url}/>
                <DocPreview label="Proof of address" path={docModal.address_doc_url}/>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DocPreview = ({ label, path }) => {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  if (!path) return <div className="p-4 border border-black/10 rounded-md text-xs text-muted-foreground">{label}: not uploaded</div>;
  const url = `${BACKEND}${path}`;
  return (
    <div className="border border-black/10 rounded-md overflow-hidden">
      <div className="p-3 text-xs font-semibold border-b border-black/5 flex items-center justify-between">
        <span>{label}</span>
        <a href={url} target="_blank" rel="noreferrer" className="text-[#C05A3C] hover:underline">Open</a>
      </div>
      <img src={url} alt={label} className="w-full max-h-72 object-contain bg-[#F3EFE9]" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
    </div>
  );
};

export default Admin;
